import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Form, Modal, Tabs, message } from "antd";
import DataState from "../../components/DataState/DataState";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import {
  fetchMilestoneTrackerData,
  filterInvoices,
  updateMilestoneStatus,
  uploadInvoiceFile,
} from "../../services/milestoneTrackerApi";
import {
  mapApiInvoiceToTrackerRow,
  mapInvoiceToTrackerRow,
  mapMilestoneToTableRow,
  resolveInvoiceStatusOption,
  toUiInvoiceStatus,
} from "../../utils/milestoneDataMapper";
import { useInvoiceSave, useTrackerData, useTrackerFilters } from "../../hooks/milestones/useMilestoneInvoice";
import { STATUS_TO_API_VALUE } from "../../config/MilestoneInvoice.constants";
import { LEADERSHIP_ATTENTION_QUERY_KEY } from "../Dashboard/partials/leadershipAttention.hooks";
import { SummaryCards } from "../../components/MilestoneAndInvoice/SummaryCards";
import { SearchAndFilter } from "../../components/MilestoneAndInvoice/SearchAndFiter";
import { MilestoneTable } from "../../components/MilestoneAndInvoice/MilestoneTable";
import { InvoiceTable } from "../../components/MilestoneAndInvoice/InvoiceTable";
import { useMilestoneColumns } from "./partials/useMilestoneColumns";
import { useInvoiceColumns } from "./partials/useInvoiceColumns";
import { UploadInvoiceModal } from "./partials/UploadInvoiceModal";
import { InvoiceStatusModal } from "./partials/InvoiceStatusModal";
import { MilestoneStatusModal } from "./partials/MilestoneStatusModal";
import { dedupeMilestones, mapApiSummaryToTrackerSummary } from "./tracker.utils";
import { DEFAULT_PAGE_SIZE, FILTER_DEBOUNCE_MS, TRACKER_TABS } from "./tracker.constants";

// Identifies one filter request (tab, query, 0-based page, page size) so a result can be matched to the current view.
const filterRequestKey = (tab, params, pageNumber, pageSize) =>
  JSON.stringify([tab, params ?? null, pageNumber, pageSize]);

export function MilestoneInvoiceTracker() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(TRACKER_TABS.MILESTONES);
  const [milestonePage, setMilestonePage] = useState(1);
  const [milestonePageSize, setMilestonePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePageSize, setInvoicePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // const [projectFilter, setProjectFilter] = useState("all");
  // const [sowFilter, setSowFilter] = useState("all");
  const [revenueMonthFilter, setRevenueMonthFilter] = useState("all");
  // const [revenueYearFilter, setRevenueYearFilter] = useState("all");
  const [milestoneStatusFilter, setMilestoneStatusFilter] = useState("all");
  const [milestoneStartDate, setMilestoneStartDate] = useState(null);
  const [milestoneEndDate, setMilestoneEndDate] = useState(null);
  const [invoiceStartDate, setInvoiceStartDate] = useState(null);
  const [invoiceEndDate, setInvoiceEndDate] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [popoverOpenKey, setPopoverOpenKey] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [milestoneModalOpen, setMilestoneModalOpen] = useState(false);
  const [activeMilestone, setActiveMilestone] = useState(null);
  const [selectedMilestoneStatus, setSelectedMilestoneStatus] = useState(undefined);
  const [uploadInvoiceModal, setUploadInvoiceModal] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const milestoneFilterRequestSeq = useRef(0);
  const invoiceFilterRequestSeq = useRef(0);
  const [filterResult, setFilterResult] = useState(null);
  const [apiFilteredInvoices, setApiFilteredInvoices] = useState(null);
  const [apiFilteredInvoicePagination, setApiFilteredInvoicePagination] = useState(null);
  const [apiFilteredMilestones, setApiFilteredMilestones] = useState(null);
  const [apiFilteredMilestonesTotal, setApiFilteredMilestonesTotal] = useState(null);
  const [apiSummaryOverride, setApiSummaryOverride] = useState(null);
  const [isPendingInvoiceFilterActive, setIsPendingInvoiceFilterActive] = useState(false);
  const [milestoneApiQuery, setMilestoneApiQuery] = useState(null);
  const [invoiceApiQuery, setInvoiceApiQuery] = useState(null);
  const [form] = Form.useForm();
  const selectedInvoiceStatus = Form.useWatch("invoiceStatus", form);
  const apiPage = activeTab === "invoices" ? invoicePage : milestonePage;
  const apiPageSize = activeTab === "invoices" ? invoicePageSize : milestonePageSize;
  const {
    milestoneRows,
    invoiceRows,
    summary,
    loadingData,
    loadError,
    isRequestPending,
    reloadTrackerData,
  } = useTrackerData(Math.max(0, apiPage - 1), apiPageSize);
  const [filterData, setFilterData] = useState([]);
  // Key of the filter request the current view needs. A result for any other key means it is still loading.
  const activeFilterKey = activeTab === "invoices"
    ? filterRequestKey("invoices", invoiceApiQuery, Math.max(0, invoicePage - 1), invoicePageSize)
    : filterRequestKey("milestones", milestoneApiQuery, Math.max(0, milestonePage - 1), milestonePageSize);
  // Derived in this render, so a new base or filter request shows the skeleton before any effect runs.
  const isFilterPending = !filterResult || filterResult.key !== activeFilterKey;
  const pageStatus = isRequestPending || isFilterPending
    ? "loading"
    : (loadError || filterResult.error)
      ? "error"
      : "ready";
  const pageError = loadError || filterResult?.error || "";

  const milestoneSearchKeyword = useMemo(
    () => {
      const trimmed = search.trim();
      return trimmed.length >= 3 ? trimmed : undefined;
    },
    [search],
  );
  const effectiveLocalSearch = useMemo(() => {
    const trimmed = search.trim();
    return trimmed.length >= 3 ? trimmed : "";
  }, [search]);
  const milestoneStartDateParam = useMemo(
    () => filterData[0]?.dateRange?.[0]?.format("MM-01-YYYY"),
    [filterData],
  );
  const milestoneEndDateParam = useMemo(
    () => filterData[0]?.dateRange?.[1]?.endOf("month").format("MM-DD-YYYY"),
    [filterData],
  );

  const invoiceRevenueMonthParam = useMemo(
    () => invoiceStartDate?.format("MM-YYYY"),
    [invoiceStartDate],
  );

  const invoiceSearchKeyword = useMemo(
    () => {
      const trimmed = search.trim();
      return trimmed.length >= 3 ? trimmed : undefined;
    },
    [search],
  );

  const milestoneQueryParams = useMemo(
    () => ({
      searchKeyword: milestoneSearchKeyword,
      milestoneStatus:
        milestoneStatusFilter !== "all" ? milestoneStatusFilter : undefined,
      startDate: milestoneStartDateParam,
      endDate: milestoneEndDateParam,
    }),
    [milestoneSearchKeyword, milestoneStatusFilter, milestoneStartDateParam, milestoneEndDateParam],
  );

  const invoiceQueryParams = useMemo(
    () => ({
      searchKeyword: invoiceSearchKeyword,
      approvalStatusInFG:
        statusFilter !== "all" ? STATUS_TO_API_VALUE[statusFilter] : undefined,
      revenueMonth: invoiceRevenueMonthParam,
    }),
    [invoiceSearchKeyword, statusFilter, invoiceRevenueMonthParam],
  );

  const { filteredMilestones, filteredInvoices } = useTrackerFilters({
    milestoneRows,
    invoiceRows,
    search: effectiveLocalSearch,
    // Invoice status filtering should happen only through Apply -> API call.
    statusFilter: "all",
    milestoneStatusFilter: activeTab === "milestones" ? milestoneStatusFilter : "all",
    projectFilter: "all",
    sowFilter: "all",
    // Invoice revenue month filtering should happen only through Apply -> API call.
    revenueMonthFilter: "all",
    revenueYearFilter: "all",
  });

  const dedupedFilteredMilestones = useMemo(
    () => dedupeMilestones(filteredMilestones),
    [filteredMilestones],
  );

  const dedupedApiFilteredMilestones = useMemo(
    () => dedupeMilestones(apiFilteredMilestones ?? []),
    [apiFilteredMilestones],
  );

  const activeFilterCount = activeTab === "milestones"
    ? [
      milestoneStatusFilter !== "all",
      milestoneStartDate !== null,
      milestoneEndDate !== null,
    ].filter(Boolean).length
    : [
      statusFilter !== "all",
      invoiceStartDate !== null,
    ].filter(Boolean).length;

  const invoicedAmount = summary.submittedAmount;
  const displayedInvoices = invoiceApiQuery
    ? (apiFilteredInvoices ?? [])
    : invoiceRows;
  const displayedMilestones = milestoneApiQuery
    ? dedupedApiFilteredMilestones
    : dedupedFilteredMilestones;
  const milestoneTableTotal = milestoneApiQuery
    ? Number(apiFilteredMilestonesTotal ?? displayedMilestones.length)
    : summary.totalMilestoneCount;
  const invoiceTableTotal = invoiceApiQuery
    ? Number(apiFilteredInvoicePagination?.totalElements ?? displayedInvoices.length)
    : summary.totalInvoiceCount;
  const summaryForCards = apiSummaryOverride ?? summary;

  const hasMilestoneApiQuery = Boolean(
    milestoneSearchKeyword ||
    milestoneStatusFilter !== "all" ||
    milestoneStartDateParam ||
    milestoneEndDateParam,
  );

  const hasInvoiceApiQuery = Boolean(
    invoiceSearchKeyword ||
    statusFilter !== "all" ||
    invoiceRevenueMonthParam,
  );

  useEffect(() => {
    if (activeTab !== "milestones") return;

    setMilestonePage(1);
    setMilestoneApiQuery((prev) => {
      const next= {
        milestoneStatus: prev?.milestoneStatus,
        startDate: prev?.startDate,
        endDate: prev?.endDate,
        searchKeyword: milestoneSearchKeyword,
      };

      if (!next.searchKeyword && !next.milestoneStatus && !next.startDate && !next.endDate) {
        return null;
      }

      return next;
    });
  }, [activeTab, milestoneSearchKeyword]);

  useEffect(() => {
    if (activeTab !== "invoices") return;

    setInvoicePage(1);
    setInvoiceApiQuery((prev) => {
      const next = {
        approvalStatusInFG: prev?.approvalStatusInFG,
        revenueMonth: prev?.revenueMonth,
        searchKeyword: invoiceSearchKeyword,
      };

      if (!next.searchKeyword && !next.approvalStatusInFG && !next.revenueMonth) {
        return null;
      }

      return next;
    });
  }, [activeTab, invoiceSearchKeyword]);

  const fetchMilestoneRows = async (
    params,
    pageNumber = Math.max(0, milestonePage - 1),
    pageSize = milestonePageSize,
  ) => {
    const requestSeq = ++milestoneFilterRequestSeq.current;
    const requestKey = filterRequestKey("milestones", params, pageNumber, pageSize);
    setFilterLoading(true);
    setFilterResult(null);
    try {
      if (!params || (!params.searchKeyword && !params.milestoneStatus && !params.startDate && !params.endDate)) {
        setApiFilteredMilestones(null);
        setApiFilteredMilestonesTotal(null);
        setApiSummaryOverride(null);
        setFilterResult({ key: requestKey, error: "" });
        return;
      }

      const data = await fetchMilestoneTrackerData(pageNumber, pageSize, params);
      if (requestSeq !== milestoneFilterRequestSeq.current) {
        return;
      }
      const milestones = Array.isArray(data.milestones) ? data.milestones : [];
      setApiFilteredMilestones(milestones.map(mapMilestoneToTableRow));
      setApiFilteredMilestonesTotal(
        Number(data.totalMilestoneCount ?? milestones.length ?? 0),
      );
      setApiSummaryOverride(mapApiSummaryToTrackerSummary(data));
      setFilterResult({ key: requestKey, error: "" });
    } catch (error) {
      if (requestSeq !== milestoneFilterRequestSeq.current) {
        return;
      }
      setFilterResult({
        key: requestKey,
        error: error instanceof Error ? error.message : "Failed to load milestone filters",
      });
    } finally {
      if (requestSeq === milestoneFilterRequestSeq.current) {
        setFilterLoading(false);
      }
    }
  };

  const fetchInvoiceRows = async (
    params,
    pageNumber = Math.max(0, invoicePage - 1),
    pageSize = invoicePageSize,
  ) => {
    const requestSeq = ++invoiceFilterRequestSeq.current;
    const requestKey = filterRequestKey("invoices", params, pageNumber, pageSize);
    setFilterLoading(true);
    setFilterResult(null);
    try {
      if (!params || (!params.approvalStatusInFG && !params.revenueMonth && !params.startDate && !params.endDate && !params.searchKeyword)) {
        setApiFilteredInvoices(null);
        setApiFilteredInvoicePagination(null);
        setApiSummaryOverride(null);
        setFilterResult({ key: requestKey, error: "" });
        return;
      }

      const result = await filterInvoices(
        params.approvalStatusInFG,
        params.revenueMonth,
        params.startDate,
        params.endDate,
        params.searchKeyword,
        pageNumber,
        pageSize,
      );
      if (requestSeq !== invoiceFilterRequestSeq.current) {
        return;
      }
      setApiFilteredInvoices(result.invoices.map(mapApiInvoiceToTrackerRow));
      setApiFilteredInvoicePagination(result.invoicePagination || null);

      const summaryData = await fetchMilestoneTrackerData(pageNumber, pageSize, {
        approvalStatusInFG: params.approvalStatusInFG,
        revenueMonth: params.revenueMonth,
        startDate: params.startDate,
        endDate: params.endDate,
        searchKeyword: params.searchKeyword,
      });
      if (requestSeq !== invoiceFilterRequestSeq.current) {
        return;
      }
      setApiSummaryOverride(mapApiSummaryToTrackerSummary(summaryData));
      setFilterResult({ key: requestKey, error: "" });
    } catch (error) {
      if (requestSeq !== invoiceFilterRequestSeq.current) {
        return;
      }
      setFilterResult({
        key: requestKey,
        error: error instanceof Error ? error.message : "Failed to load invoice filters",
      });
    } finally {
      if (requestSeq === invoiceFilterRequestSeq.current) {
        setFilterLoading(false);
      }
    }
  };

  useEffect(() => {
    if (activeTab !== "milestones") {
      return;
    }

    if (!milestoneApiQuery) {
      setApiFilteredMilestones(null);
      setApiSummaryOverride(null);
      setFilterResult({ key: filterRequestKey("milestones", null, Math.max(0, milestonePage - 1), milestonePageSize), error: "" });
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void fetchMilestoneRows(milestoneApiQuery, Math.max(0, milestonePage - 1), milestonePageSize);
    }, FILTER_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [activeTab, milestoneApiQuery, milestonePage, milestonePageSize]);

  useEffect(() => {
    if (activeTab !== "invoices") {
      return;
    }

    if (!invoiceApiQuery) {
      setApiFilteredInvoices(null);
      setApiFilteredInvoicePagination(null);
      setApiSummaryOverride(null);
      setFilterResult({ key: filterRequestKey("invoices", null, Math.max(0, invoicePage - 1), invoicePageSize), error: "" });
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void fetchInvoiceRows(invoiceApiQuery, Math.max(0, invoicePage - 1), invoicePageSize);
    }, FILTER_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [activeTab, invoiceApiQuery, invoicePage, invoicePageSize]);

  useEffect(() => {
    // For API-filtered invoices, page data comes from backend.
    // Keep selected page in a valid range based on server-provided total pages.
    if (!apiFilteredInvoices) {
      return;
    }

    const maxPage = Math.max(
      1,
      Number(
        apiFilteredInvoicePagination?.totalPages ??
        Math.ceil(displayedInvoices.length / invoicePageSize),
      ),
    );
    if (invoicePage > maxPage) {
      setInvoicePage(maxPage);
    }
  }, [
    apiFilteredInvoices,
    apiFilteredInvoicePagination?.totalPages,
    displayedInvoices.length,
    invoicePage,
    invoicePageSize,
  ]);

  // Refreshes whichever data source (unfiltered or API-filtered) is currently displayed,
  // so cards/table stay in sync after a mutation without resetting search/filter/pagination.
  const refreshTrackerAndFilters = async () => {
    const tasks= [reloadTrackerData()];

    if (milestoneApiQuery) {
      tasks.push(fetchMilestoneRows(milestoneApiQuery, Math.max(0, milestonePage - 1), milestonePageSize));
    }

    if (invoiceApiQuery) {
      tasks.push(fetchInvoiceRows(invoiceApiQuery, Math.max(0, invoicePage - 1), invoicePageSize));
    }

    await Promise.all(tasks);

    // Milestone/invoice changes can affect the leadership attention list on other pages.
    await queryClient.invalidateQueries({ queryKey: [LEADERSHIP_ATTENTION_QUERY_KEY] });
  };

  const handleApplyFilters = useCallback(async () => {
    try {
      setFilterLoading(true);

      if (activeTab === "milestones") {
        setMilestonePage(1);
        setMilestoneApiQuery(hasMilestoneApiQuery ? milestoneQueryParams : null);
        setFilterOpen(false);

        if (hasMilestoneApiQuery) {
          message.success("Milestone filters applied.");
        }
      } else {
        // API filtering for invoices
        const approvalStatusInFG = STATUS_TO_API_VALUE[statusFilter];
        setIsPendingInvoiceFilterActive(false);

        if (!approvalStatusInFG && !invoiceRevenueMonthParam && !invoiceSearchKeyword) {
          setApiFilteredInvoices(null);
          setApiFilteredInvoicePagination(null);
          setInvoiceApiQuery(null);
          setApiSummaryOverride(null);
          setFilterOpen(false);
          return;
        }

        setInvoicePage(1);
        setInvoiceApiQuery(invoiceQueryParams);
        setFilterOpen(false);
        message.success("Filtered invoices loaded.");
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Failed to apply filters");
    } finally {
      setFilterLoading(false);
    }
  }, [activeTab, hasMilestoneApiQuery, milestoneQueryParams, statusFilter, invoiceRevenueMonthParam, invoiceSearchKeyword, invoiceQueryParams]);

  const handlePendingInvoiceCardClick = useCallback(() => {
    const shouldClearPendingFilter = isPendingInvoiceFilterActive;
    setActiveTab(TRACKER_TABS.INVOICES);
    setInvoicePage(1);
    if (shouldClearPendingFilter) {
      setInvoiceApiQuery(null);
      setApiFilteredInvoices(null);
      setApiFilteredInvoicePagination(null);
      setApiSummaryOverride(null);
      setIsPendingInvoiceFilterActive(false);
      message.success("Pending invoice filter cleared.");
    } else {
      setInvoiceApiQuery({
        approvalStatusInFG: "Pending Approval,Rejected",
        searchKeyword: invoiceSearchKeyword,
      });
      setIsPendingInvoiceFilterActive(true);
      message.success("Pending invoices loaded.");
    }
    setFilterOpen(false);
  }, [isPendingInvoiceFilterActive, invoiceSearchKeyword]);

  const handleClearFilters = useCallback(async () => {
    if (activeTab === "milestones") {
      setSearch("");
      setMilestoneStatusFilter("all");
      setFilterData([]);
      setMilestoneStartDate(null);
      setMilestoneEndDate(null);
      setApiFilteredMilestones(null);
      setMilestoneApiQuery(null);
      setApiFilteredMilestonesTotal(null);
      setApiSummaryOverride(null);
      setMilestonePage(1);
      setFilterOpen(false);
      await reloadTrackerData();
    } else {
      setSearch("");
      setStatusFilter("all");
      setRevenueMonthFilter("all");
      setInvoiceStartDate(null);
      setInvoiceEndDate(null);
      setApiFilteredInvoices(null);
      setApiFilteredInvoicePagination(null);
      setInvoiceApiQuery(null);
      setApiSummaryOverride(null);
      setIsPendingInvoiceFilterActive(false);
      setInvoicePage(1);
      setFilterOpen(false);
    }
  }, [activeTab, reloadTrackerData]);

  const { handleSave } = useInvoiceSave({
    form,
    editRow,
    onClose: () => {
      setAddModal(false);
      setEditRow(null);
    },
    reloadTrackerData: refreshTrackerAndFilters,
  });

  const handleUploadInvoice = async () => {
    if (!invoiceFile) {
      message.error("Please select a file to upload");
      return;
    }

    try {
      setUploading(true);

      const uploadResult = await uploadInvoiceFile(invoiceFile);
      const processedCount = Number(
        uploadResult.successCount ?? uploadResult.processedRecordsCount ?? 0,
      );
      const failedCount = Number(
        uploadResult.failedCount ?? uploadResult.failedRecordsCount ?? 0,
      );
      const uploadMessage = uploadResult.message || "Invoice upload completed";

      if (failedCount > 0) {
        message.warning(`${uploadMessage}. ${processedCount} record(s) added, ${failedCount} record(s) failed.`);

        const failedInvoiceText = (uploadResult.failedInvoices || [])
          .map((row) => {
            const rowNos = Array.isArray(row.rowNumbers) && row.rowNumbers.length > 0
              ? row.rowNumbers.join(", ")
              : "-";
            const project = row.projectId || "-";
            const missingFields = Array.isArray(row.missingFields) && row.missingFields.length > 0
              ? row.missingFields.join(", ")
              : "Unknown error";
            return `Row(s) ${rowNos} (Project ${project}): Missing fields - ${missingFields}`;
          })
          .join("\n");

        const rowErrorText = failedInvoiceText || (uploadResult.rowErrors || [])
          .map((row) => {
            const rowNo = row.rowNumber ?? "-";
            const project = row.projectId || "-";
            const reason = Array.isArray(row.errors) && row.errors.length > 0
              ? row.errors.join(", ")
              : "Unknown error";
            return `Row ${rowNo} (Project ${project}): ${reason}`;
          })
          .join("\n");

        if (rowErrorText) {
          Modal.warning({
            title: "Some invoice rows failed",
            content: (
              <div style={{ whiteSpace: "pre-wrap", maxHeight: 260, overflowY: "auto" }}>
                {rowErrorText}
              </div>
            ),
            width: 700,
          });
        }
      } else {
        message.success(uploadMessage);
      }

      setUploadInvoiceModal(false);
      setInvoiceFile(null);
      await refreshTrackerAndFilters();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleMilestonePopoverOpenChange = useCallback((key) => {
    setPopoverOpenKey(key);
  }, []);

  const handleInvoicePopoverOpenChange = useCallback((key) => {
    setPopoverOpenKey(key);
    if (key === null) {
      setEditRow(null);
    }
  }, []);

  const handleViewMilestoneDetails = useCallback((row) => {
    setActiveMilestone(row);
  }, []);

  const handleUpdateMilestoneStatusClick = useCallback((row) => {
    setActiveMilestone(row);
    setSelectedMilestoneStatus(row.status || undefined);
    setMilestoneModalOpen(true);
  }, []);

  const handleViewInvoiceDetails = useCallback((row) => {
    setEditRow(mapInvoiceToTrackerRow(row, 0));
  }, []);

  const handleUpdateInvoiceStatusClick = useCallback(
    (row) => {
      const selectedStatus = resolveInvoiceStatusOption(row);
      setEditRow(mapInvoiceToTrackerRow(row, 0));
      form.setFieldsValue({
        invoiceStatus: selectedStatus,
        pendingReason: selectedStatus === "Invoiced" ? "" : row.reason || "",
      });
      setAddModal(true);
    },
    [form],
  );

  const milestoneColumns = useMilestoneColumns({
    popoverOpenKey,
    onPopoverOpenChange: handleMilestonePopoverOpenChange,
    onViewDetails: handleViewMilestoneDetails,
    onUpdateStatusClick: handleUpdateMilestoneStatusClick,
  });

  const invoiceColumns = useInvoiceColumns({
    popoverOpenKey,
    onPopoverOpenChange: handleInvoicePopoverOpenChange,
    onViewDetails: handleViewInvoiceDetails,
    onUpdateStatusClick: handleUpdateInvoiceStatusClick,
  });

  const handleMilestonePageChange = useCallback(
    (page,size) => {
      const nextSize = size ?? milestonePageSize;
      if (nextSize !== milestonePageSize) {
        setMilestonePageSize(nextSize);
        setMilestonePage(1);
        return;
      }
      setMilestonePage(page);
    },
    [milestonePageSize],
  );

  const handleInvoicePageChange = useCallback(
    (page, size) => {
      const nextSize = size ?? invoicePageSize;
      if (nextSize !== invoicePageSize) {
        setInvoicePageSize(nextSize);
        setInvoicePage(1);
        return;
      }
      setInvoicePage(page);
    },
    [invoicePageSize],
  );

  const handleMilestoneStatusUpdate = useCallback(async () => {
    if (!activeMilestone || !selectedMilestoneStatus) {
      message.error("Please select a milestone status.");
      return;
    }

    try {
      setFilterLoading(true);
      await updateMilestoneStatus({
        projectId: activeMilestone.projectId || "",
        milestoneId: activeMilestone.milestoneId || "",
        status: selectedMilestoneStatus,
      });
      message.success("Milestone status updated successfully!");
      setMilestoneModalOpen(false);
      setActiveMilestone(null);
      setSelectedMilestoneStatus(undefined);
      await refreshTrackerAndFilters();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Failed to update milestone status");
    } finally {
      setFilterLoading(false);
    }
  }, [activeMilestone, selectedMilestoneStatus]);

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          width: "100%",
        }}
      >
        <div style={{ flex: "0 0 75%", display: pageStatus !== "error" ? undefined : "none" }}>
          <SearchAndFilter
            search={search}
            setSearch={setSearch}
            filterOpen={filterOpen}
            setFilterOpen={setFilterOpen}
            activeFilterCount={activeFilterCount}
            filterLoading={filterLoading}
            activeTab={activeTab}
            milestoneStatusFilter={milestoneStatusFilter}
            setMilestoneStatusFilter={setMilestoneStatusFilter}
            filterData={filterData}
            setFilterData={setFilterData}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            revenueMonthFilter={revenueMonthFilter}
            setRevenueMonthFilter={setRevenueMonthFilter}
            handleApplyFilters={handleApplyFilters}
            milestoneStartDate={milestoneStartDate}
            setMilestoneStartDate={setMilestoneStartDate}
            milestoneEndDate={milestoneEndDate}
            setMilestoneEndDate={setMilestoneEndDate}
            invoiceStartDate={invoiceStartDate}
            setInvoiceStartDate={setInvoiceStartDate}
            invoiceEndDate={invoiceEndDate}
            setInvoiceEndDate={setInvoiceEndDate}
            setApiFilteredInvoices={setApiFilteredInvoices}
            setInvoicePage={setInvoicePage}
            handleClearFilters={handleClearFilters}
          />
        </div>
        <div style={{ display: pageStatus !== "error" ? "contents" : "none" }}>
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              height:40
            }}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              style={{ height: 40,width:"auto",borderRadius:"4px" ,backgroundColor:"#0958d9"}}
              onClick={() => navigate("invoice-upload")}
            >
              Upload Invoice
            </Button>
          </div>
        </div>
        </div>
      </div>
      <DataState
        status={pageStatus}
        errorTitle="Couldn't load tracker data"
        error={pageError}
        onRetry={() => void refreshTrackerAndFilters()}
        skeletonRows={6}
        skeletonCards={4}
      >




      {/* Summary Stats */}
      <SummaryCards
        totalMilestoneCount={summaryForCards.totalMilestoneCount}
        totalMilestoneAmount={summaryForCards.totalMilestoneAmount}
        submittedCount={summaryForCards.submittedCount}
        totalInvoiceCount={summaryForCards.totalInvoiceCount}
        submittedAmount={summaryForCards.submittedAmount}
        approvedCount={summaryForCards.approvedCount}
        approvedAmount={summaryForCards.approvedAmount}
        pendingCount={summaryForCards.pendingCount}
        pendingInvoiceCount={summaryForCards.pendingInvoiceCount}
        pendingInvoiceAmount={summaryForCards.pendingInvoiceAmount}
        invoiceMismatch={summaryForCards.invoiceMismatch}
        isPendingInvoiceFilterActive={isPendingInvoiceFilterActive}
        onPendingInvoiceClick={handlePendingInvoiceCardClick}
      />
      {/* Main Tables */}
      <Card
        style={{
          borderRadius: 12,
          border: "1px solid #f0f0f0",
        }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          defaultActiveKey={TRACKER_TABS.MILESTONES}
          items={[
            {
              key: TRACKER_TABS.MILESTONES,
              label: `Milestones`,
              children: (
                <MilestoneTable
                  milestoneColumns={milestoneColumns}
                  filteredMilestones={displayedMilestones}
                  loadingData={loadingData || filterLoading}
                  milestonePage={milestonePage}
                  milestonePageSize={milestonePageSize}
                  totalCount={milestoneTableTotal}
                  onPageChange={handleMilestonePageChange}
                />
              ),
            },
            {
              key: TRACKER_TABS.INVOICES,
              label: `Invoices`,
              children: (
                <InvoiceTable
                  columns={invoiceColumns}
                  dataSource={displayedInvoices}
                  loading={loadingData || filterLoading}
                  page={invoicePage}
                  pageSize={invoicePageSize}
                  totalCount={invoiceTableTotal}
                  toUiInvoiceStatus={toUiInvoiceStatus}
                  onPageChange={handleInvoicePageChange}
                />
              ),
            },
          ]}
        />
      </Card>
      </DataState>

      <UploadInvoiceModal
        open={uploadInvoiceModal}
        uploading={uploading}
        invoiceFile={invoiceFile}
        onFileSelect={setInvoiceFile}
        onOk={handleUploadInvoice}
        onCancel={() => {
          setUploadInvoiceModal(false);
          setInvoiceFile(null);
        }}
      />

      <InvoiceStatusModal
        open={addModal}
        form={form}
        editRow={editRow}
        selectedInvoiceStatus={selectedInvoiceStatus}
        onOk={handleSave}
        onCancel={() => {
          setAddModal(false);
          setEditRow(null);
          form.resetFields();
        }}
      />

      <MilestoneStatusModal
        open={milestoneModalOpen}
        selectedStatus={selectedMilestoneStatus}
        onStatusChange={setSelectedMilestoneStatus}
        onOk={handleMilestoneStatusUpdate}
        onCancel={() => {
          setMilestoneModalOpen(false);
          setActiveMilestone(null);
          setSelectedMilestoneStatus(undefined);
        }}
      />
    </div>
  );
}